import React, { useState, useRef, useEffect } from 'react';
import Profile from './Profile';
import DayStats from './DayStats';
import Stats from './Stats';
import Recommendations from './Recommendations';
import Admin from './Admin';
import MentorPanel from './MentorPanel';
import { calculateNorms } from './normCalculator';
import { detectMealType, mealNormsByType } from './mealNorms';
import { saveMeal, loadHistory, getHistoryByDate, clearCache } from './storage';
import { analyzeMeal, getAdvice, recognizeSpeech, loadProfile, verifyToken, subscribePush, getPushStatus } from './api';
import { useAuth } from './hooks/useAuth';
import { useTheme } from './ThemeContext';
import ChatPanel, { mskInfo } from './ChatPanel';
import { chatGetMyStreams } from './api';
import { Icons } from './icons';

const APP_VERSION = '0.14.3'; // версия для принудительного обновления PWA

function App() {
  const { isLoggedIn, currentUser, authError, authLoading, login, register, logout, getAuth, setAuthError } = useAuth();
  const [currentScreen, setCurrentScreen] = useState('home');
  const { colors } = useTheme();
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Profile & norms
  const [userProfile, setUserProfile] = useState({ height: 170, weight: 80, age: 35, gender: 'мужчина', activity: 'сидячий', favoriteProteins: [], excludedProteins: [] });
  const [dailyNorms, setDailyNorms] = useState({ calories: 0, protein: 0, fat: 0, carbs: 0 });
  // Серверный профиль загружен? Пока false — не даём нормы/форму подменить устаревшим localStorage.
  const [profileReady, setProfileReady] = useState(false);

  // Recording
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [editableText, setEditableText] = useState('');
  const [showEditMode, setShowEditMode] = useState(false);
  // Модальное окно с текстом push-уведомления (открывается по клику на уведомление).
  // Закрывается ТОЛЬКО кнопкой «Закрыть» — клик вне окна его не убирает.
  const [pushModal, setPushModal] = useState(null); // { title, body } | null
  const fullTranscriptRef = useRef('');
  const timerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // AI processing
  const [isProcessing, setIsProcessing] = useState(false);
  const [isGettingAdvice, setIsGettingAdvice] = useState(false);
  const [result, setResult] = useState(null);
  const [advice, setAdvice] = useState(null);
  const [error, setError] = useState(null);
  const [showButtons, setShowButtons] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [currentMealData, setCurrentMealData] = useState(null);
  const [speechkitRemaining, setSpeechkitRemaining] = useState(() => {
    const saved = localStorage.getItem('currentUser');
    if (saved) try { const u = JSON.parse(saved); return u.speechkitRemaining; } catch(e) {}
    return undefined;
  });
  const [deepseekRemaining, setDeepseekRemaining] = useState(() => {
    const saved = localStorage.getItem('currentUser');
    if (saved) try { const u = JSON.parse(saved); return u.deepseekRemaining; } catch(e) {}
    return undefined;
  });

  // Знаменатель в шапке — из тарифа, а не хардкод.
  // У custom-тарифа (промокод) свои лимиты (user.speechkitLimit / deepseekLimit),
  // иначе счётчик показывал бы «30/15» и «100/50».
  // ВАЖНО: лимиты приходят только от auth-функции (verifyToken/login) — при
  // активации промокода ее надо запросить заново, см. refreshLimitsFromServer().
  const [speechkitLimit, setSpeechkitLimit] = useState(() => {
    const saved = localStorage.getItem('currentUser');
    if (saved) try { const u = JSON.parse(saved); return u.speechkitLimit || null; } catch(e) {}
    return null;
  });
  const [deepseekLimit, setDeepseekLimit] = useState(() => {
    const saved = localStorage.getItem('currentUser');
    if (saved) try { const u = JSON.parse(saved); return u.deepseekLimit || null; } catch(e) {}
    return null;
  });

  // Базовые лимиты тарифа «Стандартный» (используются, если сервер не отдал своих)
  const DEFAULT_SPEECHKIT_LIMIT = 15;
  const DEFAULT_DEEPSEEK_LIMIT = 50;

  // Auth form state
  const [authMode, setAuthMode] = useState('login');
  const [authLogin, setAuthLogin] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authInviteCode, setAuthInviteCode] = useState('');
  // authName удалён — логин используется как отображаемое имя
  const [showPassword, setShowPassword] = useState(false);

  // Load profile on login.
  // userProfile в зависимостях НЕ ставим сознательно: иначе эффект перезапускался бы
  // на каждое обновление профиля и заново дёргал сеть. Внутри он нужен только как
  // запасной вариант, когда сервер не отдал профиль — берём актуальное значение
  // через ref, чтобы не добавлять лишнюю причину для повторного запуска.
  const userProfileRef = useRef(userProfile);
  useEffect(() => { userProfileRef.current = userProfile; }, [userProfile]);

  useEffect(() => {
    if (isLoggedIn && currentUser) {
      const auth = getAuth();
      if (!auth) return;
      loadProfile(auth.login, auth.token).then(data => {
        if (data.success && data.profile) {
          setUserProfile(data.profile);
          // Свежий профиль с сервера перезаписывает локальную копию в localStorage,
          // чтобы форма профиля никогда не показывала устаревшие/пустые данные
          localStorage.setItem('userProfile', JSON.stringify(data.profile));
          setDailyNorms(calculateNorms(data.profile));
          setProfileReady(true);
        } else {
          setDailyNorms(calculateNorms(userProfileRef.current));
        }
      }).catch(err => {
        // Сбой сети/сервера: не показываем ложную ошибку, но и не трогаем локальную копию
        console.error('Ошибка загрузки профиля:', err);
        setProfileReady(true);
      });
      loadHistory().then(result => {
        if (result && !result.success && result.error === 'invalid_token') {
          // Токен недействителен — разлогиниваем
          clearCache();
          logout();
          setAuthError('Сессия устарела. Пожалуйста, войдите заново.');
          setCurrentScreen('home');
          setUserProfile({ height: 170, weight: 80, age: 35, gender: 'мужчина', activity: 'сидячий', favoriteProteins: [], excludedProteins: [] });
          setDailyNorms({ calories: 0, protein: 0, fat: 0, carbs: 0 });
          setProfileReady(false);
        }
      });
      // Обновить остатки запросов (раздельные счётчики)
      verifyToken(auth.login, auth.token).then(data => {
        if (data.success) {
          if (typeof data.speechkitRemaining !== 'undefined') setSpeechkitRemaining(data.speechkitRemaining);
          if (typeof data.deepseekRemaining !== 'undefined') setDeepseekRemaining(data.deepseekRemaining);
          if (typeof data.speechkitLimit !== 'undefined') setSpeechkitLimit(data.speechkitLimit);
          if (typeof data.deepseekLimit !== 'undefined') setDeepseekLimit(data.deepseekLimit);
        }
      });
      // Подписка на push-уведомления
      subscribeToPush(auth);
    }
  }, [isLoggedIn, currentUser, getAuth, logout, setAuthError]);

  // Admin route
  useEffect(() => {
    if (window.location.pathname === '/admin' || window.location.pathname.endsWith('/admin.html')) setCurrentScreen('admin');
  }, []);

  // === HANDLERS ===
  const handleLogin = async (e) => {
    e.preventDefault();
    const ok = await login(authLogin, authPassword);
    if (ok) { setAuthLogin(''); setAuthPassword(''); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const ok = await register(authLogin, authPassword, authInviteCode);
    if (ok) { setAuthLogin(''); setAuthPassword(''); setAuthInviteCode(''); }
  };

  // Чат (потоки): оверлей открыт?
  const [chatOpen, setChatOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);        // число потоков с непрочитанным
  const [chatServerRole, setChatServerRole] = useState(null); // server-роль чата (admin/mentor/user)
  const chatOpenRef = useRef(false);
  useEffect(() => { chatOpenRef.current = chatOpen; }, [chatOpen]);

  // Точка привязки FAB-кнопки чата: центр колонки «Запись» в нижнем меню (последний пункт).
  // Измеряем по DOM, чтобы ровно стоять над кнопкой записи при любом числе колонок.
  const [fabAnchor, setFabAnchor] = useState(null);
  useEffect(() => {
    const measure = () => {
      const el = document.querySelector('[data-nav-key="home"]');
      if (el) {
        const r = el.getBoundingClientRect();
        setFabAnchor({ x: (r.left + r.right) / 2, y: r.top });
      } else {
        setFabAnchor(null);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, currentUser]);

  // Поллинг индикатора непрочитанного FAB (раз в 5 сек, окно 08–23 МСК), когда залогинен
  useEffect(() => {
    if (!isLoggedIn) { setChatUnread(0); return; }
    let cancelled = false;
    const tick = async () => {
      if (chatOpenRef.current) return; // пока открыт чат — индикатор обновляет сам ChatPanel
      const a = getAuth();
      if (!a) return;
      try {
        const d = await chatGetMyStreams(a.login, a.token);
        if (d.success && !cancelled) {
          setChatUnread((d.streams || []).filter(s => s.unread).length);
          setChatServerRole(d.role || a.role || 'user');
        }
      } catch (e) { /* тихо */ }
    };
    tick();
    const t = setInterval(() => {
      const nowMsk = Date.now() + mskInfo.MSK_OFFSET_MS;
      const h = new Date(nowMsk).getUTCHours();
      if (h >= 8 && h < 23) tick();
    }, mskInfo.POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, [isLoggedIn, getAuth]);

  // === Текст push-уведомления в приложении ===
  // Уведомление в системе исчезает сразу по нажатию — показываем тот же текст
  // модальным окном, чтобы можно было прочитать спокойно.
  // Два пути: service worker прислал postMessage (окно уже открыто) или
  // текст пришёл query-параметрами (вкладка открылась заново).
  useEffect(() => {
    // 1) Текст из адресной строки (открытие нового окна)
    try {
      const params = new URLSearchParams(window.location.search);
      const t = params.get('pushTitle');
      const b = params.get('pushBody');
      if (t || b) {
        setPushModal({ title: t || 'Ассистент питания', body: b || '' });
        setCurrentScreen('home');
      }
    } catch (e) { /* некритично */ }

    // 2) Сообщение от service worker (окно уже было открыто)
    const onMessage = (event) => {
      const d = event.data;
      if (d && d.type === 'PUSH_NOTIFICATION_CLICK' && d.notification) {
        setPushModal({
          title: d.notification.title || 'Ассистент питания',
          body: d.notification.body || ''
        });
        setCurrentScreen('home'); // по клику показываем главный экран
      }
    };
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', onMessage);
    }
    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', onMessage);
      }
    };
  }, []);

  const handleLogout = () => {
    clearCache();
    logout();
    setChatOpen(false);
    setChatUnread(0);
    setCurrentScreen('home');
    setUserProfile({ height: 170, weight: 80, age: 35, gender: 'мужчина', activity: 'сидячий', favoriteProteins: [], excludedProteins: [] });
    setDailyNorms({ calories: 0, protein: 0, fat: 0, carbs: 0 });
    setProfileReady(false);
    };

  const handleAgain = () => {
    setResult(null); setAdvice(null); setShowButtons(false);
    setCurrentMealData(null); setError(null); setSaveSuccess(false);
    setEditableText(''); setShowEditMode(false);
  };

  // === AI CALLS ===
  const callPrompt1 = async (rawText) => {
    const auth = getAuth();
    if (!auth) { setError('Требуется авторизация'); return; }
    setIsProcessing(true); setError(null); setResult(null); setAdvice(null);
    setShowButtons(false); setCurrentMealData(null); setSaveSuccess(false);

    try {
      // Считаем норму на этот приём (в % от дневной) и передаём через userParams.mealNorms
      const voiceMealType = detectMealType(rawText);
      const voiceMealNorm = mealNormsByType(dailyNorms, voiceMealType);
      const voiceUserParams = { ...userProfile, mealNorms: voiceMealNorm };
      const data = await analyzeMeal(auth.login, auth.token, rawText, voiceUserParams, { mealNorms: voiceMealNorm });
      if (data.success) {
        setResult({ type: 'prompt1', content: data.result });
        setCurrentMealData(data.result);
        setShowButtons(true);
      } else {
        setError(data.error || 'Ошибка обработки');
      }
      // Обновить остаток DeepSeek после AI-запроса
      if (typeof data.remaining !== 'undefined') {
        setDeepseekRemaining(data.remaining);
      }
    } catch (err) {
      setError('Не удалось связаться с сервером');
    } finally {
      setIsProcessing(false);
    }
  };

  const callPrompt2 = async (userChoice) => {
    if (!currentMealData) return;
    const auth = getAuth();
    if (!auth) { setError('Требуется авторизация'); return; }

    const today = new Date().toLocaleDateString('ru-RU');
    const todayMeals = getHistoryByDate(today);
    let dayCal = 0, dayProt = 0, dayFat = 0, dayCarb = 0;
    todayMeals.forEach(meal => {
      const d = meal.mealText;
      const cm = d.match(/Калории:\s*([\d.]+)\s*ккал/);
      const pm = d.match(/Белки:\s*([\d.]+)\s*г/);
      const fm = d.match(/Жиры:\s*([\d.]+)\s*г/);
      const cm2 = d.match(/Углеводы:\s*([\d.]+)\s*г/);
      if (cm) dayCal += parseFloat(cm[1]) || 0;
      if (pm) dayProt += parseFloat(pm[1]) || 0;
      if (fm) dayFat += parseFloat(fm[1]) || 0;
      if (cm2) dayCarb += parseFloat(cm2[1]) || 0;
    });

    setIsGettingAdvice(true); setAdvice(null);
    try {
      // Передаём норму приёма (в % от дневной) в Prompt 2 через userProfile.mealNorms
      const adviceMealType = detectMealType(currentMealData);
      const adviceMealNorm = mealNormsByType(dailyNorms, adviceMealType);
      const adviceUserParams = { ...userProfile, mealNorms: adviceMealNorm };
      const data = await getAdvice(auth.login, auth.token, currentMealData, adviceUserParams, userChoice, dailyNorms, {
        calories: Math.round(dayCal), protein: Math.round(dayProt),
        fat: Math.round(dayFat), carbs: Math.round(dayCarb),
        remaining: Math.round(dailyNorms.calories - dayCal)
      });
      if (data.success) setAdvice(data.advice);
      else setError(data.error || 'Ошибка получения совета');
      // Обновить остаток DeepSeek после AI-запроса
      if (typeof data.remaining !== 'undefined') {
        setDeepseekRemaining(data.remaining);
      }
    } catch (err) {
      setError('Не удалось получить совет');
    } finally {
      setIsGettingAdvice(false);
    }
  };

  const handleSaveMeal = async () => {
    if (currentMealData) {
      const result = await saveMeal(currentMealData, userProfile);
      if (result && result.error === 'invalid_token') {
        clearCache();
        logout();
        setAuthError('Сессия устарела. Пожалуйста, войдите заново.');
        setCurrentScreen('home');
        setUserProfile({ height: 170, weight: 80, age: 35, gender: 'мужчина', activity: 'сидячий', favoriteProteins: [], excludedProteins: [] });
        setDailyNorms({ calories: 0, protein: 0, fat: 0, carbs: 0 });
        return;
      }
      if (result) {
        setSaveSuccess(true); setShowButtons(false);
        setResult(null); setAdvice(null); setCurrentMealData(null);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setError('Не удалось сохранить приём. Проверьте соединение и попробуйте снова.');
      }
    }
  };

  const handleSendText = () => {
    const finalText = editableText.trim() || fullTranscriptRef.current.trim();
    if (finalText) { setShowEditMode(false); setEditableText(''); callPrompt1(finalText); }
    else setError('Введите текст');
  };

  // === PUSH NOTIFICATIONS ===
  const subscribeToPush = async (auth) => {
    try {
      const registration = await navigator.serviceWorker.ready;
      // Проверяем, есть ли уже подписка
      let subscription = await registration.pushManager.getSubscription();
      if (subscription) return; // уже подписан

      // Получаем VAPID-ключ с сервера
      const statusData = await getPushStatus(auth.login, auth.token);
      if (!statusData.success || !statusData.vapidPublicKey) {
        console.warn('Push not available:', statusData.error);
        return;
      }

      const vapidPublicKey = statusData.vapidPublicKey;
      const convertedKey = urlBase64ToUint8Array(vapidPublicKey);

      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedKey
      });

      // Отправляем подписку на сервер
      const subData = JSON.parse(JSON.stringify(subscription));
      await subscribePush(auth.login, auth.token, subData);
      console.log('Push subscribed');
    } catch (err) {
      console.warn('Push subscribe error:', err.message);
    }
  };

  // === RECORDING ===
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { sampleRate: 16000, channelCount: 1, echoCancellation: true, noiseSuppression: true } });
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus', audioBitsPerSecond: 64000 });
      mediaRecorderRef.current = mr;
      audioChunksRef.current = [];

      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        try {
          const wavB64 = await convertWebMToWav(blob);
          setIsProcessing(true); setError(null);
          const auth = getAuth();
          if (!auth) { setError('Требуется авторизация'); setIsProcessing(false); return; }
          const data = await recognizeSpeech(auth.login, auth.token, wavB64);
          if (data.success) {
            fullTranscriptRef.current = data.text.trim();
            setEditableText(data.text.trim());
            setShowEditMode(true);
          } else {
            setError(data.error || 'Не удалось распознать речь');
          }
        } catch (err) {
          setError('Ошибка обработки аудио: ' + err.message);
        } finally {
          setIsProcessing(false);
        }
      };

      mr.start(1000);
      setIsRecording(true); setIsPaused(false); setRecordingTime(0); setError(null);
      timerRef.current = setInterval(() => setRecordingTime(p => p + 1), 1000);
    } catch (err) {
      setError('Не удалось получить доступ к микрофону');
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop();
    setIsRecording(false); setIsPaused(false);
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') { mediaRecorderRef.current.pause(); setIsPaused(true); clearInterval(timerRef.current); }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current?.state === 'paused') {
      mediaRecorderRef.current.resume(); setIsPaused(false);
      timerRef.current = setInterval(() => setRecordingTime(p => p + 1), 1000);
    }
  };

  const cancelRecording = () => {
    clearInterval(timerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop();
    setIsRecording(false); setIsPaused(false); setRecordingTime(0);
    fullTranscriptRef.current = ''; setEditableText(''); setShowEditMode(false);
  };

  const handleMicClick = () => {
    if (isRecording) isPaused ? resumeRecording() : stopRecording();
    else startRecording();
  };

  // Кнопка «текст» рядом с микрофоном: открываем тот же экран ручного ввода,
  // по которому уже идёт распознанная диктовка (showEditMode + editableText).
  const handleTextClick = () => {
    if (isProcessing || isGettingAdvice) return;
    fullTranscriptRef.current = '';
    setEditableText('');
    setError(null);
    setShowEditMode(true);
  };

  // Знаменатели для шапки: свои лимиты тарифа, иначе базовые
  const skLimitForHeader = speechkitLimit ?? DEFAULT_SPEECHKIT_LIMIT;
  const dsLimitForHeader = deepseekLimit ?? DEFAULT_DEEPSEEK_LIMIT;

  // Перечитать лимиты с сервера. Нужно после активации промокода: сам промокод
  // отдаёт новые лимиты, но шапка и остальное состояние берут их только отсюда.
  const refreshLimitsFromServer = async () => {
    try {
      const auth = getAuth();
      if (!auth) return;
      const data = await verifyToken(auth.login, auth.token);
      if (!data.success) return;
      if (typeof data.speechkitRemaining !== 'undefined') setSpeechkitRemaining(data.speechkitRemaining);
      if (typeof data.deepseekRemaining !== 'undefined') setDeepseekRemaining(data.deepseekRemaining);
      if (typeof data.speechkitLimit !== 'undefined') setSpeechkitLimit(data.speechkitLimit);
      if (typeof data.deepseekLimit !== 'undefined') setDeepseekLimit(data.deepseekLimit);
    } catch (e) { /* молча — при следующем запросе всё равно перечитается */ }
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  // === RENDER HELPERS ===
  const renderResult = () => result ? result.content.split('\n').map((line, i) => {
    if (!line.trim()) return null;
    if (line.startsWith('Тип приёма:')) return <div key={i} style={s.mealType}>{line}</div>;
    if (line.startsWith('Продукты:')) return <div key={i} style={s.productsTitle}>{line}</div>;
    if (line.startsWith('Калории:')) return <div key={i} style={s.caloriesLine}>{line}</div>;
    if (line.startsWith('Белки:')) return <div key={i} style={s.proteinLine}>{line}</div>;
    if (line.startsWith('Соотношение')) return <div key={i} style={s.ratioLine}>{line}</div>;
    return <div key={i} style={s.resultLine}>{line}</div>;
  }) : null;

  const renderAdvice = () => advice ? (
    <div style={s.adviceBox}>
      <div style={s.adviceTitle}><Icons.recommendations size={14} color="#1D9E75" /> РЕКОМЕНДАЦИИ</div>
      {advice.split('\n').filter(l => l.trim()).map((l, i) => <div key={i} style={s.adviceLine}>{l}</div>)}
      <button style={s.saveMealBtn} onClick={handleSaveMeal}><Icons.save size={14} color="#fff" /> СОХРАНИТЬ В ИСТОРИЮ</button>
    </div>
  ) : null;

  const hasContent = !!advice;

  // === STYLES ===
  const s = {
    page: { maxWidth: 420, margin: '0 auto', height: '100%', width: '100%', background: colors.page, fontFamily: 'system-ui, -apple-system, sans-serif', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    // Модальное окно push-уведомления: клик по затемнению НЕ закрывает (обработчик не вешаем).
    pushOverlay: {
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '24px 16px', zIndex: 9999
    },
    pushModal: {
      width: '100%', maxWidth: 380, background: colors.card || '#fff',
      borderRadius: 16, padding: '20px 20px 16px', boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
      display: 'flex', flexDirection: 'column', gap: 10
    },
    pushModalTitle: { fontSize: 16, fontWeight: 600, color: colors.text, lineHeight: 1.3 },
    pushModalBody: { fontSize: 15, color: colors.text, lineHeight: 1.45, whiteSpace: 'pre-wrap' },
    pushModalBtn: {
      marginTop: 4, padding: '10px 16px', background: '#1D9E75', color: '#fff',
      border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 500, cursor: 'pointer', alignSelf: 'stretch'
    },
    header: { background: colors.header, padding: '16px 20px 14px' },
    headerTopRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
    headerLabel: { fontSize: 13, color: '#ffffffcc', fontWeight: 500 },
    headerUserRow: { display: 'flex', alignItems: 'center', gap: 8 },
    userName: { fontSize: 13, color: '#fff', fontWeight: 500 },
    logoutBtn: { background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', fontSize: 14, cursor: 'pointer', padding: '4px 8px', borderRadius: 16 },
    headerCaloriesRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 },
    headerCalories: { fontSize: 16, fontWeight: 600, color: '#fff', marginBottom: 10 },
    headerRequests: { fontSize: '12px', color: 'rgba(255,255,255,0.9)', background: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '2px 8px', fontWeight: 500 },
    macroRow: { display: 'flex', gap: 10 },
    macroBox: { flex: 1, background: 'rgba(255,255,255,0.1)', borderRadius: 10, padding: '8px 10px', textAlign: 'center' },
    macroLabel: { fontSize: 10, color: '#8899bb', marginBottom: 2 },
    macroValue: { fontSize: 15, fontWeight: 600 },
    center: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: hasContent ? 'flex-start' : 'center', padding: 20, overflowY: 'auto', overflowX: 'hidden', maxWidth: '100%', boxSizing: 'border-box' },
    scrollableContent: { flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' },
    recordContainer: { position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 18, marginBottom: 20 },
    processingCircle: { width: 110, height: 110, borderRadius: '50%', background: 'linear-gradient(145deg,#1D9E75,#0F6E56)', boxShadow: '0 4px 15px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 2 },
    spinner: { position: 'absolute', inset: 0, borderRadius: '50%', border: '4px solid rgba(255,255,255,0.25)', borderTopColor: '#ffffff', animation: 'cwspin 1s linear infinite' },
    recordBtn: { width: 110, height: 110, borderRadius: '50%', border: 'none', fontSize: 40, cursor: 'pointer', position: 'relative', zIndex: 2, color: 'white', boxShadow: '0 4px 15px rgba(0,0,0,0.2)' },
    textBtn: { width: 110, height: 110, borderRadius: '50%', border: 'none', cursor: 'pointer', position: 'relative', zIndex: 2, color: 'white', background: 'linear-gradient(145deg,#1D9E75,#0F6E56)', boxShadow: '0 4px 15px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    recordingTimer: { fontSize: 18, fontWeight: 600, color: '#e53935', marginBottom: 12 },
    recordingControls: { display: 'flex', gap: 12, marginBottom: 12 },
    controlBtn: { padding: '10px 20px', borderRadius: 25, border: 'none', fontSize: 14, fontWeight: 500, cursor: 'pointer', background: colors.card, color: colors.text },
    editContainer: { width: '100%', marginTop: 20 },
    editTextarea: { width: '100%', padding: 16, fontSize: 16, border: `1px solid ${colors.inputBorder}`, borderRadius: 16, background: colors.inputBg, color: colors.text, minHeight: 120, resize: 'vertical', boxSizing: 'border-box', marginBottom: 12 },
    editButtons: { display: 'flex', gap: 12 },
    editSendBtn: { flex: 1, padding: 14, background: '#1D9E75', color: 'white', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 500, cursor: 'pointer' },
    editCancelBtn: { flex: 1, padding: 14, background: colors.border, color: colors.text, border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 500, cursor: 'pointer' },
    hint: { fontSize: 15, fontWeight: 500, textAlign: 'center', marginBottom: 8, color: colors.textSecondary },
    example: { fontSize: 12, color: colors.textMuted, textAlign: 'center', marginTop: 20 },
    errorBox: { marginTop: 20, padding: '12px 16px', background: colors.error, borderRadius: 16, color: colors.errorText, fontSize: 14, width: '100%', textAlign: 'center' },
    successBox: { marginBottom: 16, padding: '12px 16px', background: colors.success, borderRadius: 16, color: colors.successText, fontSize: 14, width: '100%', textAlign: 'center' },
    resultBox: { marginTop: 20, padding: 16, background: colors.card, borderRadius: 16, width: '100%', textAlign: 'left' },
    adviceBox: { marginTop: 20, padding: 16, background: colors.advice, borderRadius: 16, width: '100%', borderLeft: `4px solid ${colors.adviceBorder}` },
    adviceTitle: { fontSize: 12, fontWeight: 600, color: '#1D9E75', marginBottom: 10 },
    adviceLine: { fontSize: 14, color: colors.text, marginBottom: 6 },
    saveMealBtn: { marginTop: 16, width: '100%', padding: 12, background: '#1D9E75', color: 'white', border: 'none', borderRadius: 12, cursor: 'pointer' },
    mealType: { fontSize: 16, fontWeight: 600, color: '#1D9E75', marginBottom: 8 },
    productsTitle: { fontSize: 13, fontWeight: 500, color: colors.textSecondary, marginTop: 8 },
    caloriesLine: { fontSize: 14, marginTop: 12, paddingTop: 8, borderTop: `1px solid ${colors.border}`, color: colors.text },
    proteinLine: { fontSize: 14, marginTop: 4, color: colors.text },
    ratioLine: { fontSize: 14, marginTop: 8, color: '#1D9E75' },
    resultLine: { fontSize: 14, color: colors.text },
    buttonRow: { display: 'flex', gap: 10, marginTop: 16, width: '100%' },
    actionBtn: { flex: 1, padding: '12px 8px', border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 500, cursor: 'pointer', color: 'white' },
    navBar: { background: colors.navBg, borderTop: `1px solid ${colors.navBorder}`, display: 'flex' },
    navItem: { flex: 1, padding: '10px 4px', textAlign: 'center', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center' },
    navIcon: { fontSize: 20 },
    navLabel: { fontSize: 10, marginTop: 2, fontWeight: 500 },
    authContainer: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
    authCard: { background: colors.card, borderRadius: 24, padding: '30px 24px', width: '100%', maxWidth: 360, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' },
    authTabs: { display: 'flex', gap: 10, marginBottom: 24 },
    authTab: { flex: 1, padding: 12, background: 'transparent', border: 'none', fontSize: 16, fontWeight: 500, color: colors.textMuted, cursor: 'pointer', borderBottom: '2px solid transparent' },
    authTabActive: { color: '#1D9E75', borderBottom: '2px solid #1D9E75' },
    authInput: { width: '100%', padding: '14px 16px', fontSize: 16, border: `1px solid ${colors.inputBorder}`, borderRadius: 14, background: colors.inputBg, color: colors.text, marginBottom: 16, boxSizing: 'border-box' },
    authError: { padding: 12, background: colors.error, color: colors.errorText, borderRadius: 12, fontSize: 14, marginBottom: 16, textAlign: 'center' },
    authBtn: { width: '100%', padding: 16, background: '#1D9E75', color: 'white', border: 'none', borderRadius: 14, fontSize: 16, fontWeight: 600, cursor: 'pointer' }
  };

  const waveStyle = (animDelay) => ({
    position: 'absolute', width: 110, height: 110, borderRadius: '50%',
    background: 'rgba(229,57,53,0.3)', animation: `pulse 1.5s ease-out ${animDelay}s infinite`, zIndex: 1
  });

  // Локальный keyframes для индикатора обработки: index.css трогать не нужно,
  // имя префиксовано (cwspin), чтобы не столкнуться с чужими анимациями.
  const spinKeyframes = `
    @keyframes cwspin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  `;

  const navColor = (key) => currentScreen === key ? '#1D9E75' : '#99aabb';

  // Определяем роль пользователя
  // «Подопечные» — по ПОТОКАМ, а не по role: кнопка есть, если человек наставник
  // хотя бы одного потока (или главный админ). chatServerRole приходит из chat-sync.
  const isMentor = chatServerRole === 'mentor' || chatServerRole === 'admin';

  const navItems = [
    { icon: <Icons.profile size={20} color={navColor('profile')} />, label: 'Профиль', key: 'profile' },
    { icon: <Icons.day size={20} color={navColor('day')} />, label: 'День', key: 'day' },
    { icon: <Icons.recommendations size={20} color={navColor('recommendations')} />, label: 'Советы', key: 'recommendations' },
    { icon: <Icons.stats size={20} color={navColor('stats')} />, label: 'Статистика', key: 'stats' },
    { icon: <Icons.mic size={20} color={navColor('home')} />, label: 'Запись', key: 'home' },
  ];

  // Добавляем кнопку "Подопечные" для наставника
  if (isMentor) {
    navItems.splice(1, 0, { icon: <Icons.users size={20} color={navColor('mentorStudents')} />, label: 'Подопечные', key: 'mentorStudents' });
  }

  return (
    <>
    <style>{spinKeyframes}</style>
    {/* Модальное окно с текстом push-уведомления.
        Затемнение кликом НЕ закрывается (onClick не задан) — только кнопкой «Закрыть». */}
    {pushModal && (
      <div style={s.pushOverlay}>
        <div style={s.pushModal}>
          <div style={s.pushModalTitle}>{pushModal.title}</div>
          <div style={s.pushModalBody}>{pushModal.body}</div>
          <button style={s.pushModalBtn} onClick={() => setPushModal(null)}>Закрыть</button>
        </div>
      </div>
    )}
    <div style={currentScreen === 'admin' || (currentScreen === 'mentorStudents' && isDesktop) ? { width: '100vw', minHeight: '100vh', background: colors.page, fontFamily: 'system-ui, -apple-system, sans-serif' } : s.page}>
      {currentScreen === 'admin' ? (
        <Admin onBack={() => setCurrentScreen('home')} />
      ) : currentScreen === 'mentorStudents' && isDesktop ? (
        <MentorPanel onBack={() => setCurrentScreen('home')} getAuth={getAuth} />
      ) : !isLoggedIn ? (
        <div style={s.authContainer}>
          <div style={s.authCard}>
            <div style={s.authTabs}>
              <button style={{ ...s.authTab, ...(authMode === 'login' ? s.authTabActive : {}) }} onClick={() => { setAuthMode('login'); setAuthError(''); }}>Вход</button>
              <button style={{ ...s.authTab, ...(authMode === 'register' ? s.authTabActive : {}) }} onClick={() => setAuthMode('register')}>Регистрация</button>
            </div>
            <form onSubmit={authMode === 'login' ? handleLogin : handleRegister}>
              <input style={s.authInput} placeholder="Логин" value={authLogin} onChange={e => setAuthLogin(e.target.value)} autoFocus />
              <div style={{position:'relative',width:'100%',marginBottom:16}}>
                <input style={{...s.authInput,marginBottom:0,paddingRight:40}} type={showPassword ? 'text' : 'password'} placeholder="Пароль" value={authPassword} onChange={e => setAuthPassword(e.target.value)} />
                <button type="button" style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',padding:4}} onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <Icons.eyeOff size={18} color="#99aabb" /> : <Icons.eye size={18} color="#99aabb" />}
                </button>
              </div>
              {authMode === 'register' && (
                <input style={{...s.authInput, marginBottom: 8, letterSpacing: 4, fontFamily: 'monospace'}} maxLength={6} inputMode="numeric" placeholder="Код приглашения (6 цифр)" value={authInviteCode} onChange={e => setAuthInviteCode(e.target.value.replace(/\D/g, ''))} />
              )}
              {authMode === 'register' && <div style={{fontSize:12,color:colors.textMuted,marginBottom:12,textAlign:'center'}}>Логин будет использоваться как отображаемое имя</div>}
              {authError && <div style={s.authError}>{authError}</div>}
              <button type="submit" style={{ ...s.authBtn, opacity: authLoading ? 0.7 : 1 }} disabled={authLoading}>
                {authLoading ? 'Загрузка...' : authMode === 'login' ? 'Войти' : 'Зарегистрироваться'}
              </button>
              {authMode === 'login' && (
                <div style={{textAlign:'center',marginTop:12,fontSize:12,color:colors.textMuted}}>
                  Забыли пароль? Напишите в <a href="https://max.ru/u/f9LHodD0cOKuJetV1y-JuWICh2SNk2A-T5pHVjSdw1GI1WNmvbCSs5t-3G4" target="_blank" rel="noopener noreferrer" style={{color:'#1D9E75',textDecoration:'underline',fontWeight:500}}>МАКС</a>
                </div>
              )}
            </form>
          </div>
        </div>
      ) : (
        <>
            <div style={s.header}>
            <div style={s.headerTopRow}>
              <div style={s.headerLabel}>Ассистент питания v{APP_VERSION}</div>
              <div style={s.headerUserRow}>
                <span style={s.userName}><Icons.profile size={14} color="#fff" /> {currentUser?.login}</span>
                <button style={s.logoutBtn} onClick={handleLogout}><Icons.logout size={14} color="#fff" /></button>
              </div>
            </div>
            <div style={s.headerCaloriesRow}>
              <div style={s.headerCalories}>Норма: {dailyNorms.calories} ккал</div>
              <div style={{display:'flex',gap:4,alignItems:'center'}}>
                {typeof speechkitRemaining !== 'undefined' && <div style={{...s.headerRequests, background:'rgba(255,255,255,0.12)'}}>Диктовка:{speechkitRemaining}/{skLimitForHeader}</div>}
                {typeof deepseekRemaining !== 'undefined' && <div style={{...s.headerRequests, background:'rgba(255,255,255,0.12)'}}>Анализ:{deepseekRemaining}/{dsLimitForHeader}</div>}
              </div>
            </div>
            <div style={s.macroRow}>
              <div style={s.macroBox}><div style={s.macroLabel}>Белки</div><div style={{ ...s.macroValue, color: '#5DCAA5' }}>{dailyNorms.protein} г</div></div>
              <div style={s.macroBox}><div style={s.macroLabel}>Жиры</div><div style={{ ...s.macroValue, color: '#FAC775' }}>{dailyNorms.fat} г</div></div>
              <div style={s.macroBox}><div style={s.macroLabel}>Углеводы</div><div style={{ ...s.macroValue, color: '#85B7EB' }}>{dailyNorms.carbs} г</div></div>
            </div>
          </div>

          {currentScreen === 'home' ? (
            <div style={s.center}>
              {saveSuccess && <div style={s.successBox}><Icons.check size={16} color={colors.successText} /> Приём сохранён!</div>}

              {!result && !advice && !showEditMode && (
                <>
                  <div style={s.recordContainer}>
                    {!isRecording && !isProcessing && !isGettingAdvice && (
                      <button style={s.textBtn} onClick={handleTextClick} title="Написать текстом">
                        <Icons.text size={40} color="#fff" />
                      </button>
                    )}
                    {!isProcessing && !isGettingAdvice && (
                      <button style={{ ...s.recordBtn, background: isRecording && !isPaused ? 'linear-gradient(145deg,#e53935,#b71c1c)' : 'linear-gradient(145deg,#1D9E75,#0F6E56)' }} onClick={handleMicClick} title="Диктовка голосом">
                        {isRecording ? (isPaused ? '▶' : '■') : <Icons.mic size={40} color="#fff" />}
                      </button>
                    )}
                    {(isProcessing || isGettingAdvice) && (
                      <div style={s.processingCircle} title="Идёт обработка">
                        <div style={s.spinner}></div>
                        <Icons.ellipsis size={34} color="#fff" />
                      </div>
                    )}
                    {isRecording && !isPaused && (<React.Fragment><div style={waveStyle(0)}></div><div style={waveStyle(0.5)}></div><div style={waveStyle(1)}></div></React.Fragment>)}
                  </div>
                  {isRecording && (
                    <>
                      <div style={s.recordingTimer}><span style={{color:'#e53935',marginRight:4}}>●</span> {formatTime(recordingTime)}</div>
                      <div style={s.recordingControls}>
                        {isPaused ? <button style={s.controlBtn} onClick={resumeRecording}>▶ Продолжить</button> : <button style={s.controlBtn} onClick={pauseRecording}>⏸ Пауза</button>}
                        <button style={s.controlBtn} onClick={cancelRecording}><Icons.close size={14} /> Отмена</button>
                      </div>
                    </>
                  )}
                  <div style={{ ...s.hint, color: isRecording ? '#e53935' : colors.textSecondary }}>
                    {isProcessing ? 'Идёт обработка...' : isGettingAdvice ? 'Анализирую...' : isRecording ? (isPaused ? 'Пауза' : 'Говорите...') : 'Нажмите текст или микрофон и скажите, что съели'}
                  </div>
                </>
              )}

              {showEditMode && (
                <div style={s.editContainer}>
                  <textarea style={s.editTextarea} value={editableText} onChange={e => setEditableText(e.target.value)} placeholder="Отредактируйте текст..." autoFocus />
                  <div style={s.editButtons}>
                    <button style={s.editSendBtn} onClick={handleSendText}><Icons.send size={16} color="#fff" /> Отправить</button>
                    <button style={s.editCancelBtn} onClick={() => { setShowEditMode(false); setEditableText(''); }}><Icons.close size={16} /> Отмена</button>
                  </div>
                </div>
              )}

              {error && <div style={s.errorBox}>{error}</div>}
              {result && <div style={s.resultBox}>{renderResult()}</div>}
              {advice && renderAdvice()}

              {showButtons && !advice && (
                <div style={s.buttonRow}>
                  <button style={{ ...s.actionBtn, background: '#1D9E75' }} onClick={() => callPrompt2('ate')}><Icons.check size={14} color="#fff" /> Уже съел</button>
                  <button style={{ ...s.actionBtn, background: '#FAC775', color: '#334455' }} onClick={() => callPrompt2('plan')}><Icons.edit size={14} /> Планирую</button>
                  <button style={{ ...s.actionBtn, background: colors.border, color: colors.textSecondary }} onClick={handleAgain}><Icons.refresh size={14} /> Заново</button>
                </div>
              )}

              {(advice || (result && !showButtons)) && !saveSuccess && (
                <button style={{ width: '100%', padding: 14, background: '#1D9E75', color: 'white', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 500, cursor: 'pointer', marginTop: 16 }} onClick={handleAgain}><Icons.refresh size={16} color="#fff" /> НОВАЯ ЗАПИСЬ</button>
              )}

              {!result && !advice && !showEditMode && <div style={s.example}>Например: ужин — рис 200 г, курица 150 г</div>}
            </div>
          ) : (
            <div style={s.scrollableContent}>
              {currentScreen === 'profile' && (
                profileReady
                  ? <Profile key="profile" onSave={p => { setUserProfile(p); setDailyNorms(calculateNorms(p)); }} initialData={userProfile} getAuth={getAuth} onPromoActivated={refreshLimitsFromServer} />
                  : <div style={{ padding: 24, textAlign: 'center', color: 'inherit' }}>Загрузка профиля…</div>
              )}
              {currentScreen === 'day' && <DayStats dailyNorms={dailyNorms} userProfile={userProfile} onBack={() => setCurrentScreen('home')} />}
              {currentScreen === 'recommendations' && <Recommendations dailyNorms={dailyNorms} userProfile={userProfile} onBack={() => setCurrentScreen('home')} />}
              {currentScreen === 'stats' && <Stats dailyNorms={dailyNorms} onBack={() => setCurrentScreen('home')} />}
              {currentScreen === 'mentorStudents' && !isDesktop && <MentorPanel onBack={() => setCurrentScreen('home')} getAuth={getAuth} />}
            </div>
          )}

          <div style={s.navBar}>
            {navItems.map(item => (
              <div key={item.key} data-nav-key={item.key} style={{ ...s.navItem, borderTop: currentScreen === item.key ? '2px solid #1D9E75' : '2px solid transparent' }} onClick={() => setCurrentScreen(item.key)}>
                <span style={s.navIcon}>{item.icon}</span>
                <span style={{ ...s.navLabel, color: currentScreen === item.key ? '#1D9E75' : '#99aabb' }}>{item.label}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>

      {/* === FAB-кнопка чата + оверлей === */}
      {isLoggedIn && !chatOpen && fabAnchor && (
        (() => {
          const hideFab = currentScreen === 'admin' || (currentScreen === 'mentorStudents' && isDesktop);
          if (hideFab) return null;
          const showCounter = chatServerRole === 'admin' || chatServerRole === 'mentor';
          return (
            <button
              onClick={() => setChatOpen(true)}
              title="Чат групп и потоков"
              style={{
                position: 'fixed',
                left: Math.round(fabAnchor.x - 28),
                top: Math.round(fabAnchor.y - 70),
                width: 56, height: 56, borderRadius: '50%',
                background: '#1D9E75', color: '#fff', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 16px rgba(0,0,0,0.3)', zIndex: 900
              }}
            >
              <Icons.chat size={26} color="#fff" />
              {chatUnread > 0 && (
                showCounter ? (
                  <span style={{
                    position: 'absolute', top: -4, right: -4, minWidth: 20, height: 20, borderRadius: 10,
                    background: '#e53935', color: '#fff', fontSize: 12, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px', boxSizing: 'border-box'
                  }}>{chatUnread}</span>
                ) : (
                  <span style={{ position: 'absolute', top: 7, right: 7, width: 12, height: 12, borderRadius: '50%', background: '#e53935' }} />
                )
              )}
            </button>
          );
        })()
      )}

      {isLoggedIn && chatOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 950,
          display: 'flex', justifyContent: 'center', background: colors.page
        }}>
          <div style={{ width: '100%', maxWidth: 480, height: '100%' }}>
            <ChatPanel
              getAuth={getAuth}
              onClose={() => setChatOpen(false)}
            />
            {/* После закрытия не даём поллингу чата гонять пусто */}
          </div>
        </div>
      )}
    </>
  );
}

// ========== AUDIO UTILS (keep in same file for now) ==========
async function convertWebMToWav(webmBlob) {
  const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  const arrayBuf = await webmBlob.arrayBuffer();
  const audioBuf = await audioCtx.decodeAudioData(arrayBuf);
  const targetRate = 16000;
  const offline = new OfflineAudioContext(1, audioBuf.duration * targetRate, targetRate);
  const src = offline.createBufferSource();
  src.buffer = audioBuf;
  src.connect(offline.destination);
  src.start();
  const rendered = await offline.startRendering();
  await audioCtx.close();
  const wav = audioBufferToWav(rendered, targetRate);
  const base64 = btoa(new Uint8Array(wav).reduce((d, b) => d + String.fromCharCode(b), ''));
  return base64;
}

function audioBufferToWav(buffer, sampleRate) {
  const samples = buffer.getChannelData(0);
  const wav = new ArrayBuffer(44 + samples.length * 2);
  const v = new DataView(wav);
  const write = (o, s) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
  write(0, 'RIFF'); v.setUint32(4, 36 + samples.length * 2, true); write(8, 'WAVE');
  write(12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true);
  v.setUint16(22, 1, true); v.setUint32(24, sampleRate, true);
  v.setUint32(28, sampleRate * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
  write(36, 'data'); v.setUint32(40, samples.length * 2, true);
  let off = 44;
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    v.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    off += 2;
  }
  return wav;
}

// ========== PUSH UTILS ==========
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default App;
